// src/api/warehouse.js
// Полный API складских сущностей (vehicles, meals, animals, military-technics, boats, radio-stations)
// Паттерн: list/get/create/update/delete для каждого ресурса.
// Дополнительно: createOrUpdateVehicleByName и createOrUpdateVehicleRecordWithName.

import { http } from './http';
import { checkOrCreateBookByDate } from './book';

// ===== Generic helpers =====
function resource(base) {
  return {
    list: (params = {}) => http.get(base, { params }).then(r => r.data),
    get: (id) => http.get(`${base}${id}/`).then(r => r.data),
    create: (payload) => http.post(base, payload).then(r => r.data),
    update: (id, payload) => http.patch(`${base}${id}/`, payload).then(r => r.data),
    delete: (id) => http.delete(`${base}${id}/`).then(r => r.data),
  };
}

export const vehiclesApi = resource('warehouse/vehicles/');
export const mealsApi = resource('warehouse/meals/');
export const animalsApi = resource('warehouse/animals/');
export const militaryTechnicsApi = resource('warehouse/military-technics/');
export const boatsApi = resource('warehouse/boats/');
export const radioStationsApi = resource('warehouse/radio-stations/');

// ===== Vehicle helpers =====
/**
 * Создать или обновить ТС по имени (уникальность по имени, case-insensitive).
 * @param {string} name
 * @param {string} grnz государственный номер (может быть пустым)
 * @param {number|undefined} vehicle_type ID типа (опционально)
 * @returns {Promise<object>} итоговая сущность ТС
 */
export async function createOrUpdateVehicleByName(name, grnz, vehicle_type) {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Имя ТС пусто');
  const list = await vehiclesApi.list();
  const existing = (list || []).find(v => (v.name || '').trim().toLowerCase() === trimmed.toLowerCase());
  if (existing) {
    const needUpdate = (grnz && grnz !== existing.grnz) || (vehicle_type && vehicle_type !== existing.vehicle_type);
    if (needUpdate) {
      const updated = await vehiclesApi.update(existing.id, { name: trimmed, grnz, vehicle_type });
      return updated;
    }
    return existing;
  } else {
    const created = await vehiclesApi.create({ name: trimmed, grnz, vehicle_type });
    return created;
  }
}

// ===== Meal helpers =====
/**
 * Получить или создать пайок по имени (уникальность по name, case-insensitive).
 * @param {string} name
 * @returns {Promise<object>} итоговая сущность пайка (meal)
 */
export async function createOrGetMealByName(name, meal_type, quantity) {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Название пайка пусто');
  const list = await mealsApi.list();
  const existing = (list || []).find(m => (m.name || '').trim().toLowerCase() === trimmed.toLowerCase());
  if (existing) {
    // Проверим необходимость обновления meal_type или quantity (если backend разрешает PATCH этих полей)
    const needUpdate = (
      (meal_type && meal_type !== existing.meal_type) ||
      (quantity !== undefined && Number(quantity) !== Number(existing.quantity))
    );
    if (needUpdate) {
      const patch = { name: trimmed };
      if (meal_type) patch.meal_type = meal_type;
      if (quantity !== undefined) patch.quantity = Number(quantity);
      return mealsApi.update(existing.id, patch);
    }
    return existing;
  }
  const createPayload = { name: trimmed };
  if (meal_type) createPayload.meal_type = meal_type;
  if (quantity !== undefined) createPayload.quantity = Number(quantity);
  return mealsApi.create(createPayload);
}

// ===== Vehicle Records (book/vehicle-records/) интеграция =====
// Новая схема: { book, vehicle, odometr, description }
// Обёртки обновлены в vehicleRecords.js; здесь — удобный компаунд метод.

/**
 * Создать или обновить запись пробега по имени ТС (автосоздание / апдейт ТС).
 * Если запись vehicle-record для данного book+vehicle уже существует — обновляем её.
 * @param {string|Date} date Дата для контекста книги (YYYY-MM-DD)
 * @param {number} bookId ID книги (при наличии; если нет — берётся через checkOrCreateBookByDate)
 * @param {string} vehicleName Имя ТС
 * @param {string} grnz Гос. номер
 * @param {string|number} odometr Пробег (будет приведён к числу / строке по нуждам бекенда)
 * @param {string} description Описание
 * @param {number|undefined} vehicle_type ID типа ТС (опционально)
 * @param {function} listVehicleRecordsFn Опционально передать функцию получения записей (для тестов/DI)
 * @param {function} createVehicleRecordFn Опционально — функция создания
 * @param {function} updateVehicleRecordFn Опционально — функция обновления
 */
export async function createOrUpdateVehicleRecordWithName({ date, bookId, vehicleName, grnz, odometr, description, vehicle_type, listVehicleRecordsFn, createVehicleRecordFn, updateVehicleRecordFn }) {
  let activeBookId = bookId;
  if (!activeBookId) {
    const book = await checkOrCreateBookByDate(date);
    activeBookId = book?.id || book?.book?.id || book;
    if (!activeBookId) throw new Error('Не удалось определить bookId');
  }
  const vehicle = await createOrUpdateVehicleByName(vehicleName, grnz, vehicle_type);
  if (!vehicle?.id) throw new Error('vehicle.id не получен');

  const listVehicleRecords = listVehicleRecordsFn || (await import('./vehicleRecords')).listVehicleRecords;
  const createVehicleRecord = createVehicleRecordFn || (await import('./vehicleRecords')).createVehicleRecord;
  const updateVehicleRecord = updateVehicleRecordFn || (await import('./vehicleRecords')).updateVehicleRecord;

  // Получим существующие записи по vehicle (фильтр по vehicle; book добавится автоматически в http.get через ensureContext)
  const existingRecords = await listVehicleRecords({ book: activeBookId, vehicle: vehicle.id });
  let record = Array.isArray(existingRecords) ? existingRecords[0] : null;
  const payload = { book: activeBookId, vehicle: vehicle.id, odometr, description: description || '' };

  if (record && record.id) {
    const updated = await updateVehicleRecord(record.id, payload);
    return updated;
  }
  const created = await createVehicleRecord(payload);
  return created;
}

// ===== Meal Records (book/meal-records/) интеграция =====
// Новая схема: { book, meal, quantity }
// Обёртки обновлены в mealRecords.js; здесь — удобный компаунд метод.

/**
 * Создать или обновить запись пайка по имени (автосоздание / апдейт пайка).
 * Если запись meal-record для данного book+meal уже существует — обновляем её.
 * @param {string|Date} date Дата для контекста книги (YYYY-MM-DD)
 * @param {number} bookId ID книги (при наличии; если нет — берётся через checkOrCreateBookByDate)
 * @param {string} mealName Имя пайка
 * @param {string} meal_type Тип пайка (опционально, для создания/обновления сущности Meal)
 * @param {number} mealQuantity Количество пайка (опционально, для создания/обновления сущности Meal)
 * @param {number} recordQuantity Количество в записи (обязательно)
 * @returns {Promise<object>} итоговая запись пайка (meal-record)
 */
export async function createMealAndRecord({ name, meal_type, mealQuantity, bookId, recordQuantity }) {
  if (!bookId) throw new Error('bookId обязателен для создания записи пайка');
  const meal = await createOrGetMealByName(name, meal_type, mealQuantity);
  if (!meal?.id) throw new Error('Не удалось получить id пайка после создания');
  const { createMealRecord } = await import('./mealRecords');
  const rec = await createMealRecord({ book: bookId, meal: meal.id, quantity: recordQuantity });
  return { meal, record: rec };
}
